import React, { createContext, useContext } from 'react'
import { useCandidate } from './CandidateProvider'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { cn } from '~/lib/utils'
import UploadResumeRevision from './UploadResumeRevision'
import RevisionDialog from './RevisionDialog'
import { useServiceRequests } from './ServicesSection'
import NoShowDialog from './NoShowDialog'
import { useFormatTime } from '~/hooks/useFormatTime'

const ResumeReviewContext = createContext()
const useResumeReview = () => useContext(ResumeReviewContext) || {}
export { useResumeReview }

const ResumeReviewDetail = () => {
  const { detailItemId, detailType } = useCandidate()
  const { serviceRequests } = useServiceRequests()
  const currentResumeReview =
    serviceRequests?.find(
      (serviceRequest) => serviceRequest.id == detailItemId,
    ) ||
    serviceRequests?.find(
      (serviceRequest) =>
        serviceRequest.requestType == `ServiceRequest::${detailType}`,
    )

  return (
    <ResumeReviewContext.Provider value={currentResumeReview}>
      <div className="flex items-center gap-4">
        <h2 className="font-roboto-slab text-intoo-blue-medium">
          <a href="#">Coaching Services &gt;</a>
          <span className="ml-2 underline">Resume Review Progress</span>
        </h2>
        <div className="text-lg">
          <StatusIndicator />
        </div>
      </div>
      <div className="my-4 text-lg">
        Flex Resume Reviews include an initial edit, which may be completed
        electronically or during your first meeting. The service also provides a
        second round of revisions and a final closing review to ensure the
        resume is polished and ready to use. Please note that each time you
        submit a revision, a document must be submitted.
      </div>
      <DetailTable />
    </ResumeReviewContext.Provider>
  )
}
export default ResumeReviewDetail

const DetailTable = () => {
  const { meetingViaCalendly } = useResumeReview()
  return (
    <Table
      className="w-full border-separate"
      tableClass="rounded-2xl border border-silver"
    >
      <ReviewHeading />
      <TableBody>
        <CreatedAtRow />
        <AssignmentRow />
        <ReviewFormatRow />
        {meetingViaCalendly && <PreWorkRow />}
        <InitialMeetingRow />
        <RevisionRow revisionNumber={0}>First Revision</RevisionRow>
        <SecondMeetingRow revisionNumber={0} />
        <RevisionRow revisionNumber={1}>Second Revision</RevisionRow>
        <RevisionRow revisionNumber={2}>Final Revision</RevisionRow>
      </TableBody>
    </Table>
  )
}

export const isActionable = (
  status,
  meetingViaCalendly,
  revisionNumber,
  displayStatus = null,
) => {
  const noShowOne =
    displayStatus === 'Assigned to Coach' && status === 'scheduled'
  const noShowTwo =
    displayStatus === 'Awaiting Candidate Response' &&
    status == 'revision_meeting_requested'
  if (revisionNumber === 2) {
    return true
  }
  if (noShowOne || noShowTwo) {
    return false
  }
  return (
    [
      'revision_requested',
      'scheduled',
      'revision_meeting_requested',
      'final_revision_returned',
      'revision_declined',
    ].includes(status) ||
    (!meetingViaCalendly && status === 'assigned')
  )
}

export const StatusIndicator = ({ serviceRequest }) => {
  // If serviceRequest is passed, use its fields; otherwise, use context
  const context = useResumeReview()
  const { status, displayStatus, completedAt, meetingViaCalendly } =
    serviceRequest || context

  return (
    <div>
      {isActionable(status, meetingViaCalendly, displayStatus) ? (
        <CalloutStatus>{displayStatus}</CalloutStatus>
      ) : completedAt ? (
        <CompletedOnStatus date={completedAt} />
      ) : (
        <PendingStatus>{displayStatus}</PendingStatus>
      )}
    </div>
  )
}

const useIsActionable = ({ revisionNumber }) => {
  const {
    coachRevisions,
    status,
    meetingViaCalendly,
    completedEvents,
    displayStatus,
  } = useResumeReview()
  const currentResumeReview = useResumeReview()
  const coachRevisionsLength = coachRevisions?.length || 0
  const currentRevisionIsNext =
    (coachRevisionsLength == revisionNumber &&
      !currentResumeReview.revisionDeclined) ||
    (revisionNumber === 2 && currentResumeReview?.revisionDeclined)
  const hasCompletedEvents = (completedEvents?.length || 0) > 0
  const noShowOne =
    displayStatus === 'Assigned to Coach' && status === 'scheduled'
  const noShowTwo =
    displayStatus === 'Awaiting Candidate Response' &&
    status == 'revision_meeting_requested'
  if (revisionNumber === 2 && status !== 'closed') {
    return true
  }
  if (!currentRevisionIsNext) {
    return false
  } else if (status == 'scheduled' && hasCompletedEvents) {
    return true
  } else if (!meetingViaCalendly && status === 'assigned') {
    return true
  } else if (noShowOne || noShowTwo) {
    return false
  } else if (
    ['revision_requested', 'revision_meeting_requested', 'scheduled'].includes(
      status,
    )
  ) {
    return true
  } else if (
    status === 'revision_returned' ||
    status === 'final_revision_returned' ||
    status === 'revision_declined'
  ) {
    return true
  }
  // console.log(
  //   `Current revision ${revisionNumber} is next, but not actionable.`,
  //   status,
  //   meetingViaCalendly,
  // )
}

const RevisionRow = ({ revisionNumber, children }) => {
  const { coachRevisions, dueAt } = useResumeReview()
  const currentResumeReview = useResumeReview()

  const { status } = currentResumeReview
  const finalRevision =
    status === 'closed' ? coachRevisions?.[coachRevisions?.length - 1] : null
  let currentCoachRevision = {}

  if (currentResumeReview.revisionDeclined) {
    if (revisionNumber == 1) {
      currentCoachRevision = {}
    } else if (revisionNumber == 2) {
      currentCoachRevision = coachRevisions?.[1] || {}
    } else {
      currentCoachRevision = coachRevisions?.[revisionNumber] || {}
    }
  } else if (status === 'closed') {
    currentCoachRevision = finalRevision
  } else {
    currentCoachRevision = coachRevisions?.[revisionNumber] || {}
  }

  // console.log(
  //   `Current Coach Revision for revision ${revisionNumber}:`,
  //   currentCoachRevision,
  // )

  const finalRevisionActionable = () => {
    if (status === 'closed') {
      return revisionNumber === 2
    }
    return revisionNumber === coachRevisions?.length - 1 || revisionNumber === 2
  }
  const revisionNumberMatchesRevisionsLength = finalRevisionActionable()

  const isActionable =
    useIsActionable({ revisionNumber }) && revisionNumberMatchesRevisionsLength

  const invoiceableMins = currentCoachRevision?.invoicedMinutes
  const invoiceId = currentCoachRevision?.invoiceId
  const invoiceMinuteItemId = currentCoachRevision?.invoiceMinuteItemId
  const formatTime = useFormatTime()

  const actionableText =
    revisionNumber == 2
      ? 'Upload Final and Close Review'
      : 'Upload and Provide Notes'

  const currentRevisionDueAt =
    dueAt &&
    revisionNumber ==
      (currentResumeReview?.revisionDeclined
        ? coachRevisions?.length + 1
        : coachRevisions?.length) &&
    formatTime(dueAt, 'short')

  const showDueAt =
    currentRevisionDueAt && ['scheduled', 'submitted'].includes(status)

  const hasCoachRevision = Object.keys(currentCoachRevision).length > 0

  const otherDocuments = currentCoachRevision?.otherDocuments || []
  const hasOtherDocuments = otherDocuments.length > 0
  const showDocuments =
    (hasOtherDocuments || currentCoachRevision?.url) &&
    revisionNumberMatchesRevisionsLength
  const effectiveRevisionNumber =
    currentResumeReview.revisionDeclined && revisionNumber == 2
      ? 1
      : revisionNumber

  const showCompletedStatus =
    currentCoachRevision?.createdAt && revisionNumberMatchesRevisionsLength
  const showPendingStatus =
    revisionNumber == 1 &&
    currentResumeReview.revisionDeclined &&
    revisionNumberMatchesRevisionsLength

  return (
    <StripedRow>
      <TableCell className="text-medium">{children}</TableCell>
      <TableCell>
        {showCompletedStatus ? (
          <CompletedOnStatus completedAt={currentCoachRevision.createdAt} />
        ) : showPendingStatus ? (
          <PendingStatus>Declined</PendingStatus>
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {showDueAt ? currentRevisionDueAt : <span>&mdash;</span>}
      </TableCell>
      <TableCell>
        {showDocuments ? (
          <DocumentsCol
            currentCoachRevision={currentCoachRevision}
            otherDocuments={otherDocuments}
            hasOtherDocuments={hasOtherDocuments}
          />
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-2">
          {hasCoachRevision && revisionNumberMatchesRevisionsLength && (
            <RevisionDialog
              type="Coach"
              revisionNumber={effectiveRevisionNumber}
            >
              Coach Feedback
            </RevisionDialog>
          )}
          {isActionable && (
            <UploadResumeRevision revisionNumber={revisionNumber}>
              {actionableText}
            </UploadResumeRevision>
          )}
          {!isActionable && !hasCoachRevision && <span>&mdash;</span>}
        </div>
      </TableCell>
      <TableCell>
        {invoiceableMins && revisionNumberMatchesRevisionsLength ? (
          <a
            target="_blank"
            rel="noreferrer"
            href={`/coach/invoices/${invoiceId}?highlight=${invoiceMinuteItemId}`}
          >
            {invoiceableMins}
          </a>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
    </StripedRow>
  )
}

export const DocumentsCol = ({
  currentCoachRevision,
  otherDocuments,
  hasOtherDocuments,
  preWork,
}) => {
  const url = currentCoachRevision?.url || preWork?.url
  const name = currentCoachRevision ? 'Coach Revision' : 'Pre-Work Document'
  return (
    <>
      {url && <a href={url}>{name}</a>}
      {hasOtherDocuments && (
        <div className="mt-2 flex flex-col gap-1">
          {otherDocuments.map((doc, idx) => (
            <a key={`other-doc-${idx}`} href={doc.url}>
              {doc.name}
            </a>
          ))}
        </div>
      )}
    </>
  )
}

const SecondMeetingRow = ({ revisionNumber }) => {
  const { candidateRevisions } = useResumeReview()
  const currentResumeReview = useResumeReview()

  const { completedEvents, pendingEvents } = candidateRevisions?.[0] || {}

  const meetingViaCalendly =
    candidateRevisions?.[0]?.meetingViaCalendly || false

  const pendingEventsLength = pendingEvents?.length || 0
  const completedEventsLength = completedEvents?.length || 0

  const hasPendingMeeting = pendingEventsLength > 0
  const hasCompletedEvent = completedEventsLength > 0

  const secondMeetingPending = meetingViaCalendly && hasPendingMeeting
  const secondMeetingCompleted = meetingViaCalendly && hasCompletedEvent
  const currentCandidateRevision = candidateRevisions?.[revisionNumber] || {}
  const hasCandidateRevision = Object.keys(currentCandidateRevision).length > 0
  const formatTime = useFormatTime()

  if (meetingViaCalendly) {
    const currentCompletedEvent = completedEvents?.pop()
    const currentPendingEvent = pendingEvents?.pop()

    const displayCompletedTime = currentCompletedEvent
      ? formatTime(currentCompletedEvent?.endTime, 'short')
      : null
    const scheduledForTime = currentPendingEvent
      ? formatTime(currentPendingEvent?.startTime)
      : null
    const scheduledForDate = currentPendingEvent
      ? formatTime(currentPendingEvent?.startTime, 'short')
      : null

    return (
      <StripedRow>
        <TableCell className="text-medium">Second Revision Request</TableCell>
        <TableCell>
          {secondMeetingCompleted ? (
            <CompletedOnStatus completedAt={displayCompletedTime} />
          ) : secondMeetingPending ? (
            <CompletedStatus>Scheduled for {scheduledForTime}</CompletedStatus>
          ) : (
            <PendingStatus>Awaiting Candidate</PendingStatus>
          )}
        </TableCell>
        <TableCell>{scheduledForDate || <span>&mdash;</span>}</TableCell>
        <TableCell>
          {candidateRevisions && candidateRevisions[0]?.url && (
            <a href={candidateRevisions[0]?.url}>Candidate Resume</a>
          )}
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-2">
            {hasCandidateRevision ? (
              <RevisionDialog type="Candidate" revisionNumber={revisionNumber}>
                View Request
              </RevisionDialog>
            ) : (
              <span>&mdash;</span>
            )}
          </div>
          <MeetingInfoCell />
        </TableCell>
        <TableCell>&mdash;</TableCell>
      </StripedRow>
    )
  }

  // Helper to render the common row for the last two cases
  const renderSecondMeetingRow = (statusMessage) => (
    <StripedRow>
      <TableCell className="text-medium">Second Revision Request</TableCell>
      <TableCell>
        <PendingStatus>{statusMessage}</PendingStatus>
      </TableCell>
      <TableCell>{<span>&mdash;</span>}</TableCell>
      <TableCell>
        {candidateRevisions && candidateRevisions[0]?.url ? (
          <a href={candidateRevisions[0]?.url}>Candidate Resume</a>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-2">
          {hasCandidateRevision ? (
            <RevisionDialog type="Candidate" revisionNumber={revisionNumber}>
              View Request
            </RevisionDialog>
          ) : (
            <span>&mdash;</span>
          )}
          {!isActionable && !hasCandidateRevision && <span>&mdash;</span>}
        </div>
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )

  if (currentResumeReview.status === 'returned') {
    return renderSecondMeetingRow('Awaiting Candidate')
  } else if (currentCandidateRevision.meetingViaCalendly === false) {
    return renderSecondMeetingRow('No Meeting Required')
  } else {
    return renderSecondMeetingRow('Not Started')
  }
}

const PreWorkRow = () => {
  const { preWork } = useResumeReview()
  const isActionable = !preWork

  const invoiceableMins = preWork?.invoicedMinutes
  const invoiceId = preWork?.invoiceId
  const invoiceMinuteItemId = preWork?.invoiceMinuteItemId

  const otherDocuments = preWork?.otherDocuments || []
  const hasOtherDocuments = otherDocuments.length > 0
  const showDocuments = hasOtherDocuments || preWork?.url

  const documentsCell = showDocuments ? (
    <DocumentsCol
      preWork={preWork}
      otherDocuments={otherDocuments}
      hasOtherDocuments={hasOtherDocuments}
    />
  ) : (
    'N/A'
  )

  const formatTime = useFormatTime()

  const preWorkDueAt = preWork ? (
    formatTime(preWork.createdAt, 'short')
  ) : (
    <span>&mdash;</span>
  )

  const displayCompletedTime = preWork?.createdAt
    ? formatTime(preWork.createdAt, 'short')
    : null
  return (
    <StripedRow>
      <TableCell className="text-medium">Optional Pre-Work</TableCell>
      <TableCell>
        {preWork ? (
          <CompletedOnStatus completedAt={displayCompletedTime} />
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>{preWorkDueAt}</TableCell>
      <TableCell>{documentsCell}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-2">
          {preWork && (
            <RevisionDialog type="Pre-Work" preWork={preWork}>
              View Pre-Work
            </RevisionDialog>
          )}
          {isActionable && (
            <UploadResumeRevision type="Pre-Work">
              Upload Pre-Work
            </UploadResumeRevision>
          )}
        </div>
      </TableCell>
      <TableCell>
        {(
          <a
            target="_blank"
            rel="noreferrer"
            href={`/coach/invoices/${invoiceId}?highlight=${invoiceMinuteItemId}`}
          >
            {invoiceableMins}
          </a>
        ) || <span>&mdash;</span>}
      </TableCell>
    </StripedRow>
  )
}

export const MeetingInfoCell = () => {
  const { completedEvents, pendingEvents, coachRevisions } = useResumeReview()

  const currentCompletedEvent = completedEvents?.[0]
  const currentPendingEvent = pendingEvents?.[0]

  // event has happened but there are no revisions, meaning perhaps they never met
  const showNoShowDialog = currentCompletedEvent && !coachRevisions?.length

  const { joinUrl } = currentPendingEvent || {}

  if (showNoShowDialog)
    return (
      <NoShowDialog calendlyEvent={currentCompletedEvent}>
        Mark as No-Show
      </NoShowDialog>
    )
  if (currentPendingEvent) {
    return (
      <div className="flex flex-col gap-1">
        <a href={joinUrl} target="_blank" rel="noopener noreferrer">
          Join Meeting
        </a>
        <NoShowDialog calendlyEvent={currentPendingEvent}>
          Mark as No-Show
        </NoShowDialog>
      </div>
    )
  }
  return <span>&mdash;</span>
}

const InitialMeetingRow = () => {
  const { completedEvents, pendingEvents, meetingViaCalendly } =
    useResumeReview()
  const formatTime = useFormatTime()

  const currentCompletedEvent = completedEvents?.[0]
  const currentPendingEvent = pendingEvents?.[0]
  const displayCompletedTime = currentCompletedEvent
    ? formatTime(currentCompletedEvent?.endTime, 'short')
    : null
  const scheduledForTime = currentPendingEvent
    ? formatTime(currentPendingEvent?.startTime)
    : null
  const scheduledForDate = currentPendingEvent
    ? formatTime(currentPendingEvent?.startTime, 'short')
    : null

  if (!meetingViaCalendly) {
    return (
      <StripedRow>
        <TableCell className="text-medium">Initial Meeting Info</TableCell>
        <TableCell>
          <PendingStatus>N/A</PendingStatus>
        </TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
      </StripedRow>
    )
  }

  return (
    <StripedRow>
      <TableCell className="text-medium">Initial Meeting Info</TableCell>
      <TableCell>
        {completedEvents?.length > 0 ? (
          <CompletedOnStatus completedAt={displayCompletedTime} />
        ) : pendingEvents?.length > 0 ? (
          <CompletedStatus>Scheduled for {scheduledForTime}</CompletedStatus>
        ) : (
          <PendingStatus>Awaiting Candidate</PendingStatus>
        )}
      </TableCell>
      <TableCell>{scheduledForDate || <span>&mdash;</span>}</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        <MeetingInfoCell />
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const StripedRow = ({ children, className, ...props }) => {
  return (
    <TableRow
      {...props}
      className={cn(
        'odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]',
        className,
      )}
    >
      {children}
    </TableRow>
  )
}

const ReviewFormatRow = () => {
  const { meetingViaCalendly } = useResumeReview()
  return (
    <StripedRow>
      <TableCell className="text-medium">Resume Review Format</TableCell>
      <TableCell>
        <div className="font-sm text-medium max-w-max rounded-lg bg-[#E9F2E2] px-4 py-2 text-intoo-green-coachcta">
          {meetingViaCalendly ? 'Meeting Based' : 'No Meeting Required'}
        </div>
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const CreatedAtRow = () => {
  const { createdAt, coachResumeUrl } = useResumeReview()
  return (
    <StripedRow>
      <TableCell className="text-medium">Resume Review Created</TableCell>
      <TableCell>
        {createdAt ? (
          <CompletedOnStatus completedAt={createdAt} />
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        {createdAt ? (
          <a href={coachResumeUrl} target="_blank" rel="noreferrer">
            Candidate Resume
          </a>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>
        {createdAt ? (
          <RevisionDialog type="Candidate">View Request</RevisionDialog>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const AssignmentRow = () => {
  const { assignedAt, assignmentDueDate, coachId, currentUserIsCoach } =
    useResumeReview()
  const formatTime = useFormatTime()
  const displayAssignmentDue = assignmentDueDate
    ? formatTime(assignmentDueDate, 'short')
    : null
  return (
    <StripedRow>
      <TableCell className="text-medium">Resume Review Assigned</TableCell>
      <TableCell>
        {assignedAt ? (
          <CompletedOnStatus completedAt={assignedAt} />
        ) : currentUserIsCoach ? (
          <CompletedStatus>Assigned to You</CompletedStatus>
        ) : coachId ? (
          <PendingStatus>Assigned to Another Coach</PendingStatus>
        ) : (
          <PendingStatus>Incomplete</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {assignedAt ? <span>&mdash;</span> : displayAssignmentDue}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>{assignedAt ? 5 : <span>&mdash;</span>}</TableCell>
    </StripedRow>
  )
}

export const PendingStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-[#87898A] px-4 py-2 text-white">
      {children}
    </div>
  )
}

export const CompletedOnStatus = ({ completedAt }) => {
  const formatTime = useFormatTime()
  const displayDate = formatTime(completedAt, 'short')
  if (!completedAt) {
    return <CompletedStatus>Completed</CompletedStatus>
  }
  return <CompletedStatus>Completed on {displayDate}</CompletedStatus>
}

export const CompletedStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-[#E9F2E2] px-4 py-2 text-intoo-green-coachcta">
      {children}
    </div>
  )
}

const _ActionableStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-intoo-green-coachcta px-4 py-2 text-[#E9F2E2]">
      {children}
    </div>
  )
}

export const CalloutStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-yellow-100 px-4 py-2 text-yellow-700">
      {children}
    </div>
  )
}

const ReviewHeading = () => {
  const cells = [
    'Service Progress',
    'Status',
    'Due Date',
    'Documents',
    'Actions',
    'Invoiced Time (minutes)',
  ]
  return (
    <TableHeader className="bg-intoo-blue-medium-foo h-6">
      <TableRow className="bg-intoo-blue-medium text-lg hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}
